import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import storeService from '../services/store.service';
import preferencePolicyService from '../services/preferencePolicy.service';
import { pushWidgetSnapshot } from '../hooks/useWidgetSnapshotSync';
import { syncNativeAuth } from '../utils/nativeShell';
import { Store, CreateStoreData, UpdateStoreData, CreateInvitationData, StoreInvitation, StoreContextType } from '../types/store.types';

/**
 * Store Context
 * Manages global store state
 */

const StoreContext = createContext<StoreContextType | undefined>(undefined);

interface StoreProviderProps {
  children: ReactNode;
}

export const StoreProvider: React.FC<StoreProviderProps> = ({ children }) => {
  const { user, accessToken, refreshToken, isLoading: authLoading, updateUser } = useAuth();
  const storesFetchUserIdRef = useRef<number | null>(null);
  const updateUserRef = useRef(updateUser);
  updateUserRef.current = updateUser;
  const [stores, setStores] = useState<Store[]>([]);
  const [currentStore, setCurrentStore] = useState<Store | null>(() => storeService.getCurrentStore());
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Liste des boutiques utilisée par le header (switch) : doit être chargée hors page /stores
  useEffect(() => {
    if (authLoading) return;

    if (!user) {
      storesFetchUserIdRef.current = null;
      setStores([]);
      setError(null);
      return;
    }

    const uid = user.id;
    storesFetchUserIdRef.current = uid;

    void (async () => {
      try {
        const response = await storeService.getUserStores();
        if (storesFetchUserIdRef.current !== uid) return;
        const storesList =
          response.success && response.data?.stores ? response.data.stores : [];
        setStores(storesList);
      } catch {
        if (storesFetchUserIdRef.current !== uid) return;
      }
    })();
  }, [user?.id, authLoading]);

  useEffect(() => {
    if (authLoading || !user?.id || !currentStore?.id) return;
    const storeId = currentStore.id;
    let cancelled = false;
    void (async () => {
      try {
        const result = await preferencePolicyService.apply(storeId);
        if (cancelled) return;
        if (result.changed) await updateUserRef.current?.();
        if (result.changed || result.forced) pushWidgetSnapshot();
      } catch {
        // Le magasin n'est plus accessible, ou le réseau a coupé.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, user?.id, currentStore?.id]);

  /**
   * Get all stores for current user
   * @returns The fetched stores array (so callers can use it without waiting for state update)
   */
  const getUserStores = useCallback(async (): Promise<Store[]> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await storeService.getUserStores();
      const storesList = response.success && response.data?.stores ? response.data.stores : [];
      setStores(storesList);
      return storesList;
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Impossible de charger les stores';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Select a store as current (by ID, looks up in current stores state)
   * @param storeId - Store ID to select
   */
  const selectStore = (storeId: number): void => {
    const store = stores.find(s => s.id === storeId);
    if (store) {
      setCurrentStore(store);
      storeService.saveCurrentStore(store);
      syncNativeAuth(accessToken, refreshToken, user?.id ?? null, store.id);
    }
  };

  /**
   * Set current store from a store object (e.g. after accepting an invitation when the store was just added to the list)
   * Use this when you already have the store object and want to avoid relying on state update timing.
   */
  const selectStoreByStore = (store: Store): void => {
    setCurrentStore(store);
    storeService.saveCurrentStore(store);
    syncNativeAuth(accessToken, refreshToken, user?.id ?? null, store.id);
  };

  /**
   * Create a new store
   * @param data - Store data
   * @returns Created store
   */
  const createStore = async (data: CreateStoreData): Promise<Store> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await storeService.createStore(data);

      if (response.success && response.data?.store) {
        const newStore = response.data.store;
        
        // Add to stores list
        setStores(prev => [newStore, ...prev]);
        
        // Set as current store
        setCurrentStore(newStore);
        storeService.saveCurrentStore(newStore);

        return newStore;
      }

      throw new Error(response.error?.message || 'Impossible de créer le store');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Impossible de créer le store';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Update a store
   * @param storeId - Store ID
   * @param data - Updated data
   * @returns Updated store
   */
  const updateStore = async (storeId: number, data: UpdateStoreData): Promise<Store> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await storeService.updateStore(storeId, data);

      if (response.success && response.data?.store) {
        const updatedStore = response.data.store;
        
        // Update in stores list
        setStores(prev => prev.map(s => s.id === storeId ? updatedStore : s));
        
        // Update current store if it's the same
        if (currentStore?.id === storeId) {
          setCurrentStore(updatedStore);
          storeService.saveCurrentStore(updatedStore);
        }

        return updatedStore;
      }

      throw new Error(response.error?.message || 'Impossible de modifier le store');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Impossible de modifier le store';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete a store
   * @param storeId - Store ID
   */
  const deleteStore = async (storeId: number): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await storeService.deleteStore(storeId);

      if (response.success) {
        // Remove from stores list
        setStores(prev => prev.filter(s => s.id !== storeId));
        
        // Clear current store if it's the same
        if (currentStore?.id === storeId) {
          setCurrentStore(null);
          storeService.clearCurrentStore();
        }
      } else {
        throw new Error(response.error?.message || 'Impossible de supprimer le store');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Impossible de supprimer le store';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Join a store using invitation token
   * @param invitationToken - Invitation token
   * @returns Joined store
   */
  const joinStore = async (invitationToken: string): Promise<Store> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await storeService.joinStore(invitationToken);

      if (response.success && response.data?.store) {
        const joinedStore = response.data.store;
        
        // Add to stores list if not already there
        setStores(prev => {
          const exists = prev.some(s => s.id === joinedStore.id);
          return exists ? prev : [joinedStore, ...prev];
        });

        return joinedStore;
      }

      throw new Error(response.error?.message || 'Impossible de rejoindre le store');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Impossible de rejoindre le store';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Create an invitation
   * @param storeId - Store ID
   * @param data - Invitation data
   * @returns Created invitation
   */
  const createInvitation = async (storeId: number, data: CreateInvitationData): Promise<StoreInvitation> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await storeService.createInvitation(storeId, data);

      if (response.success && response.data?.invitation) {
        return response.data.invitation;
      }

      throw new Error(response.error?.message || 'Impossible de créer l’invitation');
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Impossible de créer l’invitation';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get invitations for a store
   * @param storeId - Store ID
   * @returns Array of invitations
   */
  const getInvitations = async (storeId: number): Promise<StoreInvitation[]> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await storeService.getInvitations(storeId);

      if (response.success && response.data?.invitations) {
        return response.data.invitations;
      }

      return [];
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Impossible de charger les invitations';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Deactivate an invitation
   * @param storeId - Store ID
   * @param invitationId - Invitation ID
   */
  const deactivateInvitation = async (storeId: number, invitationId: number): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await storeService.deactivateInvitation(storeId, invitationId);

      if (!response.success) {
        throw new Error(response.error?.message || 'Impossible de désactiver l’invitation');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || err.message || 'Impossible de désactiver l’invitation';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Clear error message
   */
  const clearError = (): void => {
    setError(null);
  };

  /**
   * Clear current store (e.g. on login so the new user does not inherit previous user's store)
   */
  const clearCurrentStore = (): void => {
    setCurrentStore(null);
    storeService.clearCurrentStore();
  };

  const value: StoreContextType = {
    stores,
    currentStore,
    isLoading,
    error,
    getUserStores,
    selectStore,
    selectStoreByStore,
    createStore,
    updateStore,
    deleteStore,
    joinStore,
    createInvitation,
    getInvitations,
    deactivateInvitation,
    clearError,
    clearCurrentStore,
  };

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

/**
 * Custom hook to use store context
 * @returns StoreContextType
 */
export const useStore = (): StoreContextType => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error('useStore must be used within a StoreProvider');
  }
  return context;
};

export default StoreContext;

